import { Box, Button } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import moment from 'moment-timezone';
import React, { useState } from 'react';
import cx from 'classnames';
import { downloadMonthlyTransfers, downloadTransfers, TransferSearchType } from '../../services/api/Transfer';
import { downloadCSVFromText } from '../../services/utils';
import useSnackbar from '../../hooks/useSnackbar';
import TransferQuarterlyList from './TransferQuarterlyList';
import TransferMonthlyList, { ROW_PER_PAGE } from './TransferMonthlyList';

interface IProps {}

export enum Duration {
  Quarterly = 'quarterly',
  Monthly = 'monthly',
}

const useStyles = makeStyles((theme: Theme) => ({
  durationButton: {
    borderRadius: '18px',
    fontSize: '13px',
    marginRight: theme.spacing(2),
    marginBottom: '10px',
    color: '#999',
    '&.on': {
      boxShadow: 'none',
      backgroundColor: `${theme.palette.primary.main}18`,
      color: theme.palette.primary.main,
    },
  },
}));

const TransferList = (props: IProps): JSX.Element => {
  const classes = useStyles();
  const snackbar = useSnackbar();

  const [selectedQuarter, setSelectedQuarter] = useState<number>(0);

  const [duration, setDuration] = useState<Duration>(Duration.Quarterly);

  const [selectedMonth, setSelectedMonth] = useState<string>('');

  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [itemCount, setItemCount] = useState<number>(0);

  const [searchType, setSearchType] = useState<TransferSearchType>(TransferSearchType.StoryId);
  const [searchKey, setSearchKey] = useState<string | undefined>(undefined);

  const handleDownloadQuarterlyPaymentClick = async () => {
    try {
      const { status, data } = await downloadTransfers(selectedQuarter);

      if (status === 200) {
        let fileName = '';

        fileName =
          selectedQuarter === 0 ? moment().endOf('quarter').add(-1, 'quarter').format('YYYY_Q') : moment(selectedQuarter, 'YYYYMM').format('YYYY_Q');

        downloadCSVFromText(data, `${fileName}Q_Payment`);
      } else {
        throw new Error('Unable to download a CSV file.');
      }
    } catch (e) {
      snackbar.error(e);
    }
  };

  const handleDownloadMonthlyPaymentClick = async () => {
    try {
      const { status, data } = await downloadMonthlyTransfers(Number(`${selectedMonth}`), page + 1, ROW_PER_PAGE, true, {
        searchKey: searchKey ? Number(searchKey) : undefined,
        searchType: searchKey ? searchType : undefined,
      });

      if (status === 200) {
        let fileName = '';

        fileName = moment(selectedMonth, 'YYYYMM').format('YYYY_MMM');
        downloadCSVFromText(data, `${fileName}_Payment`);
      } else {
        throw new Error('Unable to download a CSV file.');
      }
    } catch (e) {
      snackbar.error(e);
    }
  };

  const handleDownloadReport = async () => {
    try {
      const { status, data } = await downloadMonthlyTransfers(Number(`${selectedMonth}`), page + 1, ROW_PER_PAGE, false, {
        searchKey: searchKey ? Number(searchKey) : undefined,
        searchType: searchKey ? searchType : undefined,
      });

      if (status === 200) {
        const pageNumber = `${ROW_PER_PAGE * page + 1}-${itemCount + ROW_PER_PAGE * page}of${totalCount}`;
        const fileName = moment(selectedMonth, 'YYYYMM').format('YYYY_MMM');

        downloadCSVFromText(data, `${fileName}_Payment_${pageNumber}`);
      } else {
        throw new Error('Unable to download a CSV file.');
      }
    } catch (e) {
      snackbar.error(e);
    }
  };

  return (
    <Box>
      <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div>
          <Button className={cx(classes.durationButton, { on: duration === Duration.Quarterly })} onClick={() => setDuration(Duration.Quarterly)}>
            Quarterly
          </Button>
          <Button className={cx(classes.durationButton, { on: duration === Duration.Monthly })} onClick={() => setDuration(Duration.Monthly)}>
            Monthly
          </Button>
        </div>
        <div>
          {duration === Duration.Monthly && (
            <Button variant="outlined" size="small" color="primary" onClick={handleDownloadReport} style={{ marginRight: '5px' }}>
              Download Report
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            color="primary"
            onClick={duration === Duration.Quarterly ? handleDownloadQuarterlyPaymentClick : handleDownloadMonthlyPaymentClick}
          >
            Download Payment CSV
          </Button>
        </div>
      </Box>
      {duration === Duration.Quarterly ? (
        <TransferQuarterlyList quarterStateChanger={setSelectedQuarter} />
      ) : (
        <TransferMonthlyList
          itemCountChanger={setItemCount}
          totalCountChanger={setTotalCount}
          monthStateChanger={setSelectedMonth}
          pageStateChanger={setPage}
          searchTypeChanger={setSearchType}
          searchKeywordChanger={setSearchKey}
        />
      )}
    </Box>
  );
};

export default TransferList;
